import React, { useState, useRef, useEffect } from 'react';
import { Transition } from '@headlessui/react';

// Enhanced Button Component with Micro-interactions
interface EnhancedButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  ripple?: boolean;
}

export const EnhancedButton: React.FC<EnhancedButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  onClick,
  type = 'button',
  className = '',
  leftIcon,
  rightIcon,
  ripple = true
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const [ripples, setRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;

    // Ripple effect
    if (ripple && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const newRipple = { id: Date.now(), x, y };
      
      setRipples(prev => [...prev, newRipple]);
      
      // Remove ripple after animation
      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== newRipple.id));
      }, 600);
    }

    onClick?.();
  };

  const getVariantClasses = () => {
    const baseClasses = 'relative overflow-hidden font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
    
    switch (variant) {
      case 'primary':
        return `${baseClasses} bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-md hover:shadow-lg focus:ring-primary-500`;
      case 'secondary':
        return `${baseClasses} bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md focus:ring-gray-500`;
      case 'success':
        return `${baseClasses} bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-md hover:shadow-lg focus:ring-green-500`;
      case 'danger':
        return `${baseClasses} bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-md hover:shadow-lg focus:ring-red-500`;
      case 'ghost':
        return `${baseClasses} text-gray-700 hover:bg-gray-100 focus:ring-gray-500`;
      default:
        return baseClasses;
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-sm';
      case 'lg':
        return 'px-6 py-3 text-lg';
      default:
        return 'px-4 py-2 text-base';
    }
  };

  return (
    <button
      ref={buttonRef}
      type={type}
      onClick={handleClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      disabled={disabled || loading}
      className={`
        ${getVariantClasses()}
        ${getSizeClasses()}
        ${isPressed ? 'scale-95' : 'scale-100'}
        ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
    >
      {/* Ripple effects */}
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="absolute bg-white bg-opacity-30 rounded-full animate-ping"
          style={{
            left: ripple.x - 10,
            top: ripple.y - 10,
            width: 20,
            height: 20,
            animationDuration: '0.6s'
          }}
        />
      ))}

      {/* Button content */}
      <span className="relative flex items-center justify-center space-x-2">
        {loading ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : leftIcon}
        
        {children && <span>{children}</span>}
        
        {!loading && rightIcon}
      </span>
    </button>
  );
};

// Enhanced Card Component with Hover Effects
interface EnhancedCardProps {
  children: React.ReactNode;
  hover?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  className?: string;
  padding?: 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
}

export const EnhancedCard: React.FC<EnhancedCardProps> = ({
  children,
  hover = true,
  clickable = false,
  onClick,
  className = '',
  padding = 'md',
  shadow = 'md'
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getPaddingClasses = () => {
    switch (padding) {
      case 'sm': return 'p-4';
      case 'lg': return 'p-8';
      default: return 'p-6';
    }
  };

  const getShadowClasses = () => {
    const baseClass = shadow === 'none' ? '' : 'shadow-soft';
    if (!hover) return baseClass;
    
    switch (shadow) {
      case 'sm': return `${baseClass} hover:shadow-md`;
      case 'lg': return `${baseClass} hover:shadow-xl`;
      case 'none': return 'hover:shadow-md';
      default: return `${baseClass} hover:shadow-lg`;
    }
  };

  return (
    <div
      className={`
        bg-white rounded-xl border border-gray-100 transition-all duration-200
        ${getShadowClasses()}
        ${getPaddingClasses()}
        ${hover ? 'hover:-translate-y-1' : ''}
        ${clickable ? 'cursor-pointer' : ''}
        ${isHovered && hover ? 'ring-2 ring-primary-100' : ''}
        ${className}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

// Enhanced Input Component with Focus Effects
interface EnhancedInputProps {
  label?: string;
  placeholder?: string;
  type?: string;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  icon?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const EnhancedInput: React.FC<EnhancedInputProps> = ({
  label,
  placeholder,
  type = 'text',
  value,
  onChange,
  error,
  icon,
  required = false,
  disabled = false,
  className = ''
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  useEffect(() => {
    setHasValue(!!value);
  }, [value]);

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className={`
          block text-sm font-medium transition-colors duration-200
          ${isFocused ? 'text-primary-600' : 'text-gray-700'}
          ${error ? 'text-red-600' : ''}
        `}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {icon && (
          <div className={`
            absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors duration-200
            ${isFocused ? 'text-primary-500' : 'text-gray-400'}
          `}>
            {icon}
          </div>
        )}
        
        <input
          type={type}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full rounded-lg border transition-all duration-200 px-3 py-2.5
            ${icon ? 'pl-10' : ''}
            ${isFocused ? 'border-primary-300 ring-2 ring-primary-100' : 'border-gray-300'}
            ${error ? 'border-red-300 ring-2 ring-red-100' : ''}
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}
            focus:outline-none
            ${hasValue ? 'shadow-sm' : ''}
          `}
        />
        
        {/* Animated underline */}
        <div className={`
          absolute bottom-0 left-0 h-0.5 bg-primary-500 transition-all duration-300
          ${isFocused ? 'w-full' : 'w-0'}
        `} />
      </div>
      
      {error && (
        <Transition
          show={!!error}
          enter="transition-all duration-200 ease-out"
          enterFrom="opacity-0 -translate-y-1"
          enterTo="opacity-100 translate-y-0"
        >
          <p className="text-sm text-red-600">{error}</p>
        </Transition>
      )}
    </div>
  );
};

// Floating Action Button
interface FloatingActionButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'success' | 'danger';
  className?: string;
}

export const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  children,
  onClick,
  position = 'bottom-right',
  size = 'md',
  color = 'primary',
  className = ''
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-left': return 'fixed bottom-6 left-6';
      case 'top-right': return 'fixed top-6 right-6';
      case 'top-left': return 'fixed top-6 left-6';
      default: return 'fixed bottom-6 right-6';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'w-12 h-12';
      case 'lg': return 'w-16 h-16';
      default: return 'w-14 h-14';
    }
  };

  const getColorClasses = () => {
    switch (color) {
      case 'secondary': return 'bg-secondary-500 hover:bg-secondary-600 text-white shadow-secondary-500/25';
      case 'success': return 'bg-green-500 hover:bg-green-600 text-white shadow-green-500/25';
      case 'danger': return 'bg-red-500 hover:bg-red-600 text-white shadow-red-500/25';
      default: return 'bg-primary-500 hover:bg-primary-600 text-white shadow-primary-500/25';
    }
  };

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        ${getPositionClasses()}
        ${getSizeClasses()}
        ${getColorClasses()}
        rounded-full shadow-lg hover:shadow-xl transition-all duration-200
        flex items-center justify-center z-50
        ${isHovered ? 'scale-110' : 'scale-100'}
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
        ${className}
      `}
    >
      {children}
    </button>
  );
};

// Progress Bar with Animation
interface ProgressBarProps {
  value: number;
  max?: number;
  color?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  animated?: boolean;
  showLabel?: boolean;
  className?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  color = 'primary',
  size = 'md',
  animated = true,
  showLabel = false,
  className = ''
}) => {
  const percentage = Math.min((value / max) * 100, 100);

  const getColorClasses = () => {
    switch (color) {
      case 'secondary': return 'bg-secondary-500';
      case 'success': return 'bg-green-500';
      case 'danger': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      default: return 'bg-primary-500';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'h-1';
      case 'lg': return 'h-3';
      default: return 'h-2';
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {showLabel && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-500">{Math.round(percentage)}%</span>
        </div>
      )}
      
      <div className={`bg-gray-200 rounded-full overflow-hidden ${getSizeClasses()}`}>
        <div
          className={`
            ${getColorClasses()}
            ${getSizeClasses()}
            rounded-full transition-all duration-500 ease-out
            ${animated ? 'bg-gradient-to-r from-current via-current to-transparent bg-[length:200%_100%] animate-pulse' : ''}
          `}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default EnhancedButton;
